import React, { useEffect, useId, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { styled } from 'sensortower-components/src/utils/styled';
import ErrorBoundary from 'sensortower-components/src/base-components/ErrorBoundary';
import Text from 'sensortower-components/src/base-components/Text';
import BLOCKS from 'sensortower-components/src/base-components/Text/BLOCKS';
import type { TextProps } from 'sensortower-components/src/base-components/Text/Text.types';
import { KnownVisitor } from './KnownVisitor';
import { sidekick } from '../../utils/sidekick';
import { navigation } from '../../utils/path';
import type { Locale } from '../../types';
import { FormProps, FormFieldProps, FormFieldValue } from './Form.types';
import { MARKETING_OPT_IN_FIELD_NAME, UNSUBSCRIBE_FORM_TYPE } from './utils/constants';
import { getFieldsForType, resolveFormType, fieldValidators } from './utils/fields';
import FormFieldControl, {
  Caption,
  ErrorSlot,
  RequiredMark,
  FIELD_FONT_SIZE,
  FIELD_LINE_HEIGHT,
  focusRingStyles,
} from './FormField';
import { useLocalizationContext } from '../LocalizationContext';
import {
  readSubmittedFormData,
  storeSubmittedFormData,
  clearSubmittedFormData,
  cookieDataToFormValues,
  formValuesToCookieData,
} from './utils/persistFormValues';
import { captureFormAttribution, getSubmissionAttribution } from './utils/formAttribution';
import { logFormSubmitEvent } from './utils/tracking';
import { buildFormSubmissionPayload } from './utils/submissionPayload';
import { submitFormPayload } from './utils/submitFormPayload';
import {
  getLocalizedFieldLabel,
  getLocalizedFieldPlaceholder,
  getValidationMessage,
  getSubmitMessage,
} from './utils/localization';

type RichTextNode = { nodeType?: string };

// The disclaimer is the opt-in checkbox's <label>, which permits only phrasing content, so its
// paragraphs render as block-displayed spans rather than <p>. The required mark goes inside the
// last paragraph, so it ends the text instead of wrapping onto a line of its own.
const disclaimerRenderNode = (body: TextProps['body']) => {
  const paragraphs: RichTextNode[] = (body?.json?.content ?? []).filter(
    (node: RichTextNode) => node.nodeType === BLOCKS.PARAGRAPH,
  );
  const lastParagraph = paragraphs[paragraphs.length - 1];

  return {
    [BLOCKS.PARAGRAPH]: (node: unknown, children: React.ReactNode) => (
      <Typography component="span" variant="body1" sx={{ display: 'block' }}>
        {children}
        {node === lastParagraph && <RequiredMark />}
      </Typography>
    ),
  };
};

// Through sx rather than a styled selector because the Text theme override pads every
// Typography by spacing(2) at the same specificity, and sx is serialized after it. Shares the
// fields' text size; consecutive paragraphs keep a small gap.
const DISCLAIMER_TEXT_SX: SxProps<Theme> = {
  '& .MuiTypography-root': {
    paddingBottom: 0,
    fontSize: FIELD_FONT_SIZE,
    lineHeight: FIELD_LINE_HEIGHT,
    color: 'common.white',
  },
  '& .MuiTypography-root + .MuiTypography-root': { marginTop: 1 },
  // Rich-text links are Typography too; element plus class outranks the white above. Underlined
  // because teal on white text is under the 3:1 needed to mark a link by colour alone (WCAG 1.4.1).
  // MuiTypography's pre-wrap keeps the space where a link wraps, and underlines it; normal drops it.
  '& a.MuiTypography-root, & a': { color: 'teal.main', textDecoration: 'underline', whiteSpace: 'normal' },
};

const getFieldValueFromFormData = (field: FormFieldProps, formData: FormData): FormFieldValue => {
  if (!field.fieldName) return '';

  const value = formData.get(field.fieldName);
  return typeof value === 'string' ? value : '';
};

export const Form = ({
  type,
  sidekickLookup,
  formTitle,
  formId,
  submitLabel,
  submitSuccessMessage,
  submitAction,
  redirectPath,
  privacyPolicyDisclaimer,
  productInterest,
}: FormProps) => {
  const { locale } = useRouter() as { locale: Locale };
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formValuesFromCookie, setFormValuesFromCookie] = useState(() => readSubmittedFormData());
  const [formResetKey, setFormResetKey] = useState(0);
  const storedValues = cookieDataToFormValues(formValuesFromCookie);
  const successMessageRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // Set by a failed submit and consumed once the errors have rendered. A ref, not state: the
  // move must not repeat for the per-keystroke validation, which would steal focus while typing.
  const focusFirstErrorRef = useRef(false);

  useEffect(() => {
    captureFormAttribution(window.location.search);
  }, []);

  useEffect(() => {
    // Move focus to the confirmation when the form unmounts, so keyboard and
    // screen reader users are told the submission succeeded.
    if (submitted) successMessageRef.current?.focus();
  }, [submitted]);

  useEffect(() => {
    // After a failed submit, take the visitor to the first invalid control so the error is
    // heard with the field it belongs to — the opt-in's message alone doesn't say what to accept.
    if (!focusFirstErrorRef.current) return;
    focusFirstErrorRef.current = false;
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [errors]);

  const clearFormValuesFromCookie = () => {
    clearSubmittedFormData();
    setFormValuesFromCookie({});
    // The fields remount empty, so any errors raised against the prefilled values are stale.
    setErrors({});
    setFormResetKey((key) => key + 1);
  };

  // `type` is required in Contentful, so this is always a real form type in practice.
  const formType = resolveFormType(type) ?? '';
  const fields = getFieldsForType(formType, productInterest);
  const localization = useLocalizationContext();
  // The banner greets the visitor by name and clears the prefill. A form that collects no
  // name (unsubscribe) has nothing for it to prefill, so the greeting is a non sequitur there.
  const prefillsKnownVisitor = fields.some((field) => field.fieldName === 'firstName');
  // The opt-in checkbox is labelled by the Contentful disclaimer text, so it can only render
  // when that text exists. The unsubscribe form never shows it: an opt-in on an opt-out form
  // is a contradiction — submitting it revokes consent instead.
  const showsMarketingOptIn = Boolean(privacyPolicyDisclaimer) && formType !== UNSUBSCRIBE_FORM_TYPE;
  // useId keeps the ids unique when the same Form entry renders more than once on a page —
  // otherwise both labels and aria-describedby would resolve to the first instance's controls.
  const uid = useId();
  const optInId = `${formId || 'form'}-marketing-opt-in-${uid}`;
  const optInErrorId = `${optInId}-error`;
  const submitErrorId = `${formId || 'form'}-submit-error-${uid}`;

  const getFieldError = (field: FormFieldProps, value: FormFieldValue): string | undefined => {
    if (field.isRequired && !value.trim()) {
      return getValidationMessage('required', locale);
    }

    if (field.maxLength && value.length > field.maxLength) {
      return getValidationMessage('maxLength', locale, { max: field.maxLength });
    }

    return field.fieldName ? fieldValidators[field.fieldName]?.(value, locale, field) : undefined;
  };

  const validateField = (fieldName: string, value: FormFieldValue) => {
    const field = fields.find((f) => f.fieldName === fieldName);
    if (!field) return;

    const error = getFieldError(field, value);

    setErrors((prev) => {
      if (error) return { ...prev, [fieldName]: error };
      const { [fieldName]: _, ...rest } = prev;
      return rest;
    });
  };

  // Mirrors the fields' validate-on-change behavior: unchecking shows the error
  // immediately, checking clears it.
  const handleOptInChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.currentTarget;
    setErrors((prev) => {
      if (!checked) return { ...prev, [MARKETING_OPT_IN_FIELD_NAME]: getValidationMessage('consentRequired', locale) };
      const { [MARKETING_OPT_IN_FIELD_NAME]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    event.preventDefault();
    // The disabled button alone is not enough — an Enter-key submit already in
    // flight can still land a second submit event before the re-render.
    if (isSubmitting) return;

    // Read before any await: the form element can be gone from the event by then.
    const formData = new FormData(event.currentTarget);
    const newErrors: Record<string, string> = {};
    const values: Record<string, FormFieldValue> = {};

    fields.forEach((field) => {
      if (!field.fieldName) return;
      const value = getFieldValueFromFormData(field, formData);
      values[field.fieldName] = value;
      const error = getFieldError(field, value);
      if (error) newErrors[field.fieldName] = error;
    });

    // A checked native checkbox submits 'on'; an unchecked one is absent from FormData.
    const marketingOptIn = showsMarketingOptIn && formData.get(MARKETING_OPT_IN_FIELD_NAME) === 'on';
    if (showsMarketingOptIn && !marketingOptIn) {
      newErrors[MARKETING_OPT_IN_FIELD_NAME] = getValidationMessage('consentRequired', locale);
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      focusFirstErrorRef.current = true;
      return;
    }

    const payload = buildFormSubmissionPayload({
      formValues: values,
      formType,
      locale,
      marketingOptIn,
      attribution: getSubmissionAttribution(),
    });

    setSubmitError(null);
    setIsSubmitting(true);
    const result = await submitFormPayload(payload);

    if (result !== 'success') {
      setSubmitError(getSubmitMessage(result === 'rateLimited' ? 'submitRateLimited' : 'submitFailed', locale));
      setIsSubmitting(false);
      return;
    }

    // Side effects only once the server confirmed the submission — a failed request
    // must not write the known-visitor cookie or log a GTM conversion.
    storeSubmittedFormData(formValuesToCookieData(values));
    logFormSubmitEvent(formType);

    if (submitAction === 'Redirect' && redirectPath) {
      // isSubmitting stays true so the button remains disabled while the browser navigates.
      navigation.redirectToPath(redirectPath, locale);
      return;
    }

    setSubmitted(true);
  };

  return (
    <ErrorBoundary>
      <Root>
        <Card {...sidekick(sidekickLookup)} data-testid="Form">
          {formTitle && !submitted && (
            <Title variant="h2" {...sidekick(sidekickLookup, 'formTitle')}>
              {formTitle}
            </Title>
          )}

          {submitted ? (
            <SuccessMessage role="status" tabIndex={-1} ref={successMessageRef} data-testid="Form-success">
              {submitSuccessMessage ? (
                <Text {...submitSuccessMessage} />
              ) : (
                <Typography>
                  {localization['form.fallbackSubmitSuccessMessage']?.shortTextValue ??
                    'Thank you! Your submission has been received.'}
                </Typography>
              )}
            </SuccessMessage>
          ) : (
            <FormElement
              key={formResetKey}
              ref={formRef}
              id={formId}
              noValidate
              onSubmit={handleSubmit}
              aria-label={formTitle}
              data-testid="Form-element"
            >
              {prefillsKnownVisitor && formValuesFromCookie.FirstName ? (
                <Box sx={{ mb: 2 }}>
                  <KnownVisitor formValues={formValuesFromCookie} onClearFormValues={clearFormValuesFromCookie} />
                </Box>
              ) : null}

              <FieldGrid data-testid="Form-fields">
                {fields.map((field, index) => (
                  <FormFieldControl
                    key={field.id || index}
                    field={{
                      ...field,
                      label: getLocalizedFieldLabel(field, locale),
                      placeholder: getLocalizedFieldPlaceholder(field, locale),
                    }}
                    formId={formId}
                    index={index}
                    locale={locale}
                    error={field.fieldName ? errors[field.fieldName] : undefined}
                    onValidate={validateField}
                    defaultValue={field.fieldName ? storedValues[field.fieldName] : undefined}
                  />
                ))}
              </FieldGrid>

              {showsMarketingOptIn && privacyPolicyDisclaimer && (
                <>
                  <OptInRow {...sidekick(sidekickLookup, 'privacyPolicyDisclaimer')} data-testid="Form-disclaimer">
                    <OptInCheckbox
                      id={optInId}
                      type="checkbox"
                      name={MARKETING_OPT_IN_FIELD_NAME}
                      // Semantic only (the form is noValidate): tells assistive tech the box must
                      // be checked before submit, matching required={isRequired} on the fields.
                      required
                      onChange={handleOptInChange}
                      aria-invalid={!!errors[MARKETING_OPT_IN_FIELD_NAME]}
                      aria-describedby={errors[MARKETING_OPT_IN_FIELD_NAME] ? optInErrorId : undefined}
                    />
                    {/* Clicking anywhere in the disclaimer text toggles the checkbox; browsers
                        exempt the links inside it, which just navigate. */}
                    <OptInLabel htmlFor={optInId}>
                      <Text
                        {...privacyPolicyDisclaimer}
                        component="span"
                        renderNode={disclaimerRenderNode(privacyPolicyDisclaimer.body)}
                        sx={DISCLAIMER_TEXT_SX}
                      />
                    </OptInLabel>
                  </OptInRow>
                  <ErrorSlot>
                    {errors[MARKETING_OPT_IN_FIELD_NAME] && (
                      <Caption id={optInErrorId} role="alert" isError data-testid="Form-opt-in-error">
                        {errors[MARKETING_OPT_IN_FIELD_NAME]}
                      </Caption>
                    )}
                  </ErrorSlot>
                </>
              )}

              {/* aria-disabled instead of disabled: a hard disable would drop focus to <body>
                  mid-submit, so the "Submitting…" name change is never announced and a failure
                  strands keyboard users with no focus position. Re-entry is blocked by the
                  handleSubmit guard instead. */}
              <SubmitButton
                type="submit"
                variant="contained"
                disableElevation
                aria-disabled={isSubmitting}
                aria-busy={isSubmitting}
                aria-describedby={submitError ? submitErrorId : undefined}
              >
                {isSubmitting ? getSubmitMessage('submitting', locale) : submitLabel || 'Submit'}
              </SubmitButton>

              {submitError && (
                <SubmitError id={submitErrorId} role="alert" isError data-testid="Form-submit-error">
                  {submitError}
                </SubmitError>
              )}
            </FormElement>
          )}
        </Card>
      </Root>
    </ErrorBoundary>
  );
};

export default Form;

// The Form is usually placed in one column of a Section, so how wide it actually
// renders has little to do with the viewport width — at a 784px viewport this card
// is 237px wide. Style on the card's own width instead of the viewport. 440px puts the
// demo pages' form (453px at a 1280px viewport) in two columns, which lifts its submit
// button above the fold; each field is still over 180px wide there.
const WIDE = '@container form (min-width: 440px)';

// The fixed header's height, which focusPageForm scrolls the card clear of.
const HEADER_HEIGHT = 64;

const Root = styled(Box, {
  name: 'Form',
  slot: 'Root',
})({
  containerType: 'inline-size',
  containerName: 'form',
  margin: '0 auto',
  maxWidth: '600px',
});

const Card = styled(Box, {
  name: 'Form',
  slot: 'Card',
})(({ theme }) => ({
  backgroundColor: theme.palette.blue.main,
  borderRadius: 33,
  color: theme.palette.common.white,
  padding: theme.spacing(4, 3),
  scrollMarginTop: `calc(${HEADER_HEIGHT}px + ${theme.spacing(2)})`,
  [WIDE]: {
    padding: theme.spacing(5, 4),
  },
}));

const Title = styled(Typography, {
  name: 'Form',
  slot: 'Title',
})(({ theme }) => ({
  fontWeight: 700,
  fontSize: '36px',
  lineHeight: 1.2,
  marginBottom: theme.spacing(3),
  color: `${theme.palette.common.white} !important`,
  [WIDE]: {
    fontSize: '46px',
    marginBottom: theme.spacing(4),
  },
}));

const FormElement = styled('form', {
  name: 'Form',
  slot: 'FormElement',
})(() => ({
  width: '100%',
}));

const FieldGrid = styled(Box, {
  name: 'Form',
  slot: 'FieldGrid',
})(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'start',
  columnGap: theme.spacing(3),
  // Small, because each field reserves a line for its error message (see ErrorSlot),
  // and that reserved line provides most of the visual gutter between rows.
  rowGap: theme.spacing(0.5),
  [WIDE]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}));

const OptInRow = styled(Box, {
  name: 'Form',
  slot: 'OptInRow',
})(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  // Matches FieldGrid's rowGap: the last field's ErrorSlot above already provides the gutter.
  margin: theme.spacing(0.5, 0, 0),
}));

// WCAG 2.5.8 minimum pointer target; also within a pixel of centred on the label's 22.4px line.
const OPT_IN_CHECKBOX_SIZE = 24;

const OptInCheckbox = styled('input', {
  name: 'Form',
  slot: 'OptInCheckbox',
})(({ theme }) => ({
  width: OPT_IN_CHECKBOX_SIZE,
  height: OPT_IN_CHECKBOX_SIZE,
  flexShrink: 0,
  accentColor: theme.palette.teal.main,
  cursor: 'pointer',
  // A wider gap than the fields': when checked, the fill is the same teal as the ring, so the
  // strip of card colour between them is what makes it read as a ring.
  '&:focus-visible': { ...focusRingStyles(theme), outlineOffset: 2 },
}));

const OptInLabel = styled('label', {
  name: 'Form',
  slot: 'OptInLabel',
})({
  cursor: 'pointer',
});

const SubmitButton = styled(Button, {
  name: 'Form',
  slot: 'SubmitButton',
})(({ theme }) => ({
  width: '100%',
  // The theme gives MuiButton a global minWidth of 290px and only clears it below
  // the md *viewport* breakpoint, so in a narrow column the button overflows the card.
  minWidth: 0,
  // Size and weight come from the theme's MuiButton, as on every other button on the site. Navy
  // on teal is 7.9:1; white would be 2:1.
  backgroundColor: theme.palette.teal.main,
  color: theme.palette.blue.main,
  borderRadius: 50,
  // Small, because the opt-in ErrorSlot above already reserves a line; no bottom margin, so the
  // card's own padding is the space below the button, as it is above the first field. The theme
  // adds one to every MuiButton below md.
  margin: theme.spacing(1, 0, 0),
  [theme.breakpoints.down('md')]: {
    marginBottom: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.teal.dark,
  },
  // Keyed on aria-disabled because the button stays focusable during submit (see the
  // JSX comment); dimmed teal rather than MUI's grey, which disappears on the blue card.
  '&[aria-disabled="true"]': {
    backgroundColor: theme.palette.teal.main,
    color: theme.palette.blue.main,
    opacity: 0.6,
  },
}));

// The field error look from FormField, close under the button so it reads as attached to it.
const SubmitError = styled(Caption, {
  name: 'Form',
  slot: 'SubmitError',
})(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(1.5),
}));

const SuccessMessage = styled(Box, {
  name: 'Form',
  slot: 'SuccessMessage',
})(({ theme }) => ({
  outline: 'none',
  '& img': {
    width: '40%',
  },
  // Every heading level, not just h1: most migrated success messages open with an h3, which otherwise
  // keeps the theme's dark heading colour on the dark card. The doubled class (0,2,1) outranks the
  // `& .MuiTypography-body1` (0,2,0) in `gatedResourceFormVariant` (theme/variants/createSectionVariants.ts),
  // which beat a single `& p` and turned paragraphs dark grey. Raise this if that rule gets more specific.
  '&& h1, && h2, && h3, && h4, && h5, && h6, && p': {
    color: theme.palette.common.white,
  },
  // Links (e.g. "watch the recording here") set their own dark colour, so they need one that shows on
  // the dark card. Underlined too, since colour alone isn't enough to mark a link (WCAG 1.4.1).
  // Link entries with a button variant keep their own styling.
  '& a:not(.MuiButton-root)': {
    color: theme.palette.teal.main,
    textDecoration: 'underline',
  },
}));
